import { curry } from 'ramda'
import i18next from 'i18next'
import moment from 'moment'
import { isEmpty } from 'lodash'
import { isInterpolation } from './interpolation'

export const API_DATE_FORMAT = 'YYYY MM DD HH:mm:ss.SSSZ'
export const API_DATE_TIME_REGEX = /(\d{4}-\d{2}-\d{2})(?:T\d{2}:\d{2}:\d{2}.+)?/g

export const formatDateWithUtc = curry((format, date) => date && moment(date).utc().format(format))

export const formatDate = curry((format, date) => date && moment(date, API_DATE_FORMAT).format(format))

const breakpointNames = ['minute', 'hour', 'day', 'month']
export const fromNow = (date, options = {}) => {
  const { breakpoints, hidePrefix } = options
  const bareDate = moment(date, API_DATE_FORMAT).fromNow(hidePrefix)
  if (!date) return false
  if (!breakpoints) return bareDate

  const customPrefix = hidePrefix ? '' : 'Há '

  for (let i = 0; i < breakpointNames.length; i++) {
    const breakpointName = breakpointNames[i]
    const breakpoint = breakpoints[breakpointName]
    const diff = diffFromNow(date, breakpointName)
    if (!!breakpoint && diff > 0 && diff <= breakpoint) {
      const label = i18next.t(breakpointName, { count: diff })
      return `${customPrefix}${diff} ${label}`
    }
  }

  return bareDate
}

export const diffFromNow = (date, diffFormat) =>
  date && moment(new Date()).diff(moment(date, API_DATE_FORMAT), diffFormat)

export const formatRunningTime = v => {
  v = v || 0
  if (v > 60000) return `${(v / 60000).toFixed(2)}m`
  if (v > 1000) return `${(v / 1000).toFixed(2)}s`
  return `${v.toFixed(2)}ms`
}

export const formatLongRunningTime = v => {
  v = v || 0

  const duration = moment.duration(v)
  const sec = duration.seconds()
  const min = duration.minutes()
  const hr = duration.hours()
  return `${hr}h ${min}m ${sec}s`
}

export const formatApiDate = value => {
  if (!value) return null
  if (isInterpolation(value)) return value

  const m = moment(value, ['DD/MM/YYYY HH:mm', moment.ISO_8601], true)
  if (!m.isValid()) return null

  return m.utc().format()
}

const isOnFullPeriod = (init, end, type) => {
  if (!init.isSame(end, type)) return false
  if (!init.isSame(moment(init).startOf(type), 'day')) return false
  if (!end.isSame(moment(end).endOf(type), 'day')) return false
  return true
}

export const formatDateRangeLabel = value => {
  let [init, end] = value
  init = moment(init)
  end = moment(end)

  if (init.isSame(end, 'day')) return init.format('DD MMM YYYY')
  if (isOnFullPeriod(init, end, 'month')) return init.format('MMMM YYYY')
  if (isOnFullPeriod(init, end, 'year')) return init.format('YYYY')

  return init.format('DD MMM YYYY') + ' - ' + end.format('DD MMM YYYY')
}

export const formatApiTime = value => {
  if (!value || isEmpty(value)) return value
  return moment(value, 'HH:mm').utc().format('HH:mm')
}

export const formatLocalTime = value => {
  if (!value || isEmpty(value)) return value
  return moment.utc(value, 'HH:mm').local().format('HH:mm')
}

export const isDateTime = value => {
  return !!String(value).match(API_DATE_TIME_REGEX)
}

export const genRangeRelativeDateValues = value => {
  if (!value) return undefined

  if (!value.type) return undefined

  try {
    const { type, init = 0, end = 0, mode = 'subtract', range_init } = value

    if (!type) return undefined

    const currentDate = moment()

    if (!!range_init && mode === 'add') {
      return [
        currentDate.clone().add(range_init, type).startOf('day').toDate(),
        currentDate.clone().add(end, type).endOf('day').toDate()
      ]
    } else if (!!range_init && mode === 'subtract') {
      return [
        currentDate.clone().subtract(init, type).startOf('day').toDate(),
        currentDate.clone().subtract(range_init, type).endOf('day').toDate()
      ]
    }

    return genRelativeDateValues(value)
  } catch (error) {
    console.error('Error in genRangeRelativeDateValues:', error)
    return undefined
  }
}

export const genRelativeDateValues = value => {
  if (!value || Array.isArray(value)) {
    if (Array.isArray(value) && value.length === 0) {
      return undefined
    }
    return value
  }

  if (value && value.range_init !== undefined) {
    return genRangeRelativeDateValues(value)
  }

  if (!value.type) return undefined

  const { type, init = 0, end = 0, mode = 'subtract' } = value
  const currentDate = moment()

  if (!type) return undefined

  return [
    currentDate.clone()[mode](init, type).startOf(type).toDate(),
    currentDate.clone()[mode](end, type).endOf(type).toDate()
  ]
}

export const convertFilterToRelativeDates = filters => {
  if (!filters) return

  const dateKeys = ['endDate', 'insertedAt', 'scheduling']
  const hasDateKeys = dateKeys.some(key => key in filters)

  if (!hasDateKeys) return filters

  return Object.fromEntries(Object.entries(filters).map(([key, values]) => [key, genRelativeDateValues(values)]))
}
